import React, { useReducer, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { GatsbyImage } from 'gatsby-plugin-image';
import { Typography } from '@mui/material';
import NewsBanner, { BannerWrapper, shouldDisplayNewsBannerInBooking } from 'components/common/NewsBanner';

import Button from 'mui/components/elements/DS_ButtonLink';
import CancellationSurvey from 'components/Booking-new/modules/Steps/Reschedule/components/CancellationSurvey';
import CancellationSuccessModal from 'components/Booking-new/modules/Steps/Reschedule/components/CancellationSuccessModal';
import SummaryList from 'components/Booking-new/modules/Steps/shared/SummaryList';

import { canReschedule } from 'components/Booking-new/utils/steps/reschedule';
import { getLocationByCode } from 'components/Booking-new/utils/steps/location';
import Login from '../../PersonalData/Variants/components/Login';
import NewBookingButton from '../newBookingButton/NewBookingButton';
import { rescheduleReducer } from '../hooks/rescheduleReducer';
import { useUserProfile, useServiceAllowed } from '../../PersonalData/Variants/components/Login/hooks';
import { StyledLoaderSpinner } from '../../shared/PersonalForm/styles';

import {
  ColumnSummary,
  StyledImage,
  StyledInitialCancelledWrapper,
  StyledRescheduledButton,
  StyledCancelledWrapper,
  StyledAlert,
  StyledWrapperLoader,
  StyledRescheduleWrapper,
} from './styles';

const VariantA = ({
  stepRescheduleCF,
  userData,
  service,
  serviceName,
  goToNextStep,
  showNewsBanner,
  cancelBooking,
  handleReasonChange,
}) => {
  const { allowsService, isChecking } = useServiceAllowed(service.code);

  const { setAuthToken, authToken } = useUserProfile({ allowsService });
  const isReschedulingAvailable = canReschedule(userData?.appointmentStatus, userData?.time);

  const newsBannerVisible = shouldDisplayNewsBannerInBooking(stepRescheduleCF.newsBanner, showNewsBanner);
  const location = getLocationByCode(userData?.locationCode);

  const [questionText, buttonText] = stepRescheduleCF?.cancelQuestion?.split('?') || [];
  const isCancellingAvailable = userData?.appointmentStatus === 'Scheduled';
  const newBookingAvailable =
    userData?.appointmentStatus === 'No show' ||
    userData?.appointmentStatus === 'Cancelled' ||
    userData?.appointmentStatus === 'Rescheduled' ||
    userData?.appointmentStatus === 'Completed';
  const [isCancelationActive, setCancelationActive] = useState(stepRescheduleCF?.enableCancellationFlow);
  const noBookingAvailable = userData?.appointmentStatus === 'Cancelled' || userData?.appointmentStatus === 'No show';

  const currentTime = new Date();
  const appointmentTime = new Date(userData?.time);
  const timeDifference = appointmentTime - currentTime;

  useEffect(() => {
    if (!stepRescheduleCF?.hoursToCancel || !stepRescheduleCF?.enableCancellationFlow) return;

    if (timeDifference > 0 && timeDifference > stepRescheduleCF?.hoursToCancel * 3600 * 1000)
      setCancelationActive(true);
    else setCancelationActive(false);
  }, [userData?.time, stepRescheduleCF?.hoursToCancel]);

  const [state, dispatch] = useReducer(rescheduleReducer, {
    openCancelAppointmentModal: false,
    isCancelled: false,
    wantsToCancel: false,
    showSurvey: false,
    typeButton: 'initial',
    isCancelling: false,
  });

  const { openCancelAppointmentModal, isCancelled, wantsToCancel, showSurvey, typeButton, isCancelling } = state;

  const onCancelBooking = () => {
    dispatch({ type: 'START_LOADING_FOR_CANCELLATION' });

    cancelBooking().then(
      () => {
        dispatch({ type: 'OPEN_CANCEL_APPOINTMENT_MODAL' });
        dispatch({ type: 'STOP_LOADING_FOR_CANCELLATION' });
      },
      () => {
        dispatch({ type: 'STOP_LOADING_FOR_CANCELLATION' });
      },
    );

    return false;
  };

  return (
    <section>
      {newsBannerVisible && (
        <BannerWrapper>
          <NewsBanner newsBanner={stepRescheduleCF?.newsBanner} overWriteBackColor={true} />
        </BannerWrapper>
      )}
      <StyledRescheduleWrapper>
        <div>
          <ColumnSummary>
            {showSurvey && !isCancelled ? (
              <>
                {isChecking ? (
                  <StyledLoaderSpinner />
                ) : !allowsService && !authToken ? (
                  <Login
                    setAuthToken={setAuthToken}
                    loginButtonLabel={service.loginButtonLabel}
                    failedLoginMessage={service.failedLoginMessage}
                    forgotPassword={service.forgotPassword}
                    emailLabel={service.emailSignInLabel}
                    emailError={service.emailSignInError}
                  />
                ) : (
                  <CancellationSurvey
                    data={stepRescheduleCF.surveyBeforeCancelling}
                    name={userData?.stepPersonalData.firstName}
                    dispatch={dispatch}
                    handleChange={handleReasonChange}
                    selectedValue={stepRescheduleCF.selectedReason}
                  />
                )}
              </>
            ) : (
              <>
                {wantsToCancel && !isCancelled ? (
                  <>
                    <Typography component="h1" variant="DS_h3">
                      {stepRescheduleCF?.GoodbyeSentence}
                      {userData?.stepPersonalData.firstName}!
                    </Typography>
                    <StyledAlert severity="error">{stepRescheduleCF?.cancellationNote}</StyledAlert>
                  </>
                ) : (
                  stepRescheduleCF?.title && (
                    <Typography component="h1" variant="DS_h3" data-test="booking-reschedule-title">
                      {stepRescheduleCF.title}
                    </Typography>
                  )
                )}
                <SummaryList
                  serviceName={serviceName}
                  stepTimeData={userData}
                  stepLocationData={location}
                  noBookingAvailable={noBookingAvailable || isCancelled}
                  timeOut={timeDifference < 0}
                  completed={userData?.appointmentStatus === 'Completed'}
                />
              </>
            )}

            {!isCancelled &&
              stepRescheduleCF?.enableCancellationFlow &&
              (wantsToCancel && !showSurvey ? (
                <StyledCancelledWrapper data-test="wrapper-first-reschedule">
                  <Button
                    buttonVariant="outlined"
                    buttonSize={false}
                    onClick={() => dispatch({ type: 'SHOW_SURVEY' })}
                    aria-label="cancel booking"
                    title={stepRescheduleCF?.cancelButtonLabel}
                  />
                  <Button
                    buttonSize={false}
                    onClick={goToNextStep}
                    aria-label="Reschedule booking"
                    title={stepRescheduleCF?.button}
                  />
                </StyledCancelledWrapper>
              ) : typeButton === 'initial' ? (
                <>
                  {isCancelationActive && isCancellingAvailable && timeDifference > 0 && (
                    <StyledInitialCancelledWrapper>
                      <Typography variant="DS_copyMediumRegular" component="p">
                        {questionText}?
                      </Typography>
                      <Typography
                        variant="DS_copyMediumRegular"
                        component="button"
                        aria-label="cancel booking"
                        onClick={() => dispatch({ type: 'CANCEL' })}
                      >
                        {buttonText}
                      </Typography>
                    </StyledInitialCancelledWrapper>
                  )}
                </>
              ) : typeButton === 'Reschedule' ? (
                <StyledCancelledWrapper data-test="wrapper-reschedule">
                  <StyledRescheduledButton
                    selectedValue={stepRescheduleCF.selectedReason}
                    onClick={goToNextStep}
                    aria-label="Reschedule booking"
                    title={stepRescheduleCF?.button}
                    buttonSize={false}
                  ></StyledRescheduledButton>
                  {isCancelling ? (
                    <StyledWrapperLoader>
                      <StyledLoaderSpinner color="#266ff2" />
                    </StyledWrapperLoader>
                  ) : (
                    <Button
                      buttonVariant="outlined"
                      buttonSize={false}
                      disabled={!stepRescheduleCF.selectedReason}
                      aria-label="cancel booking"
                      onClick={onCancelBooking}
                      title={stepRescheduleCF?.cancelButtonLabel}
                    />
                  )}
                </StyledCancelledWrapper>
              ) : typeButton === 'Cancel' ? (
                isCancelling ? (
                  <StyledWrapperLoader>
                    <StyledLoaderSpinner color="#266ff2" />
                  </StyledWrapperLoader>
                ) : (
                  <Button
                    buttonSize={false}
                    buttonVariant="outlined"
                    disabled={!stepRescheduleCF.selectedReason}
                    aria-label="cancel booking"
                    onClick={onCancelBooking}
                    title={stepRescheduleCF?.cancelButtonLabel}
                  ></Button>
                )
              ) : null)}

            {isReschedulingAvailable && !newBookingAvailable && !wantsToCancel && (
              <Button
                buttonSize={false}
                onClick={goToNextStep}
                aria-label="Reschedule booking"
                title={stepRescheduleCF?.button}
              />
            )}

            {newBookingAvailable && (
              <NewBookingButton
                aria-label="new booking"
                buttonSize={false}
                title={stepRescheduleCF?.button || 'Make new Appointment'}
              />
            )}

            {stepRescheduleCF?.enableCancellationFlow && (
              <CancellationSuccessModal
                data={stepRescheduleCF.successModal}
                open={openCancelAppointmentModal}
                handleClose={() => dispatch({ type: 'CLOSE_CANCEL_APPOINTMENT_MODAL' })}
              />
            )}
          </ColumnSummary>

          <StyledImage>
            {stepRescheduleCF?.image?.gatsbyImageData && (
              <GatsbyImage
                image={stepRescheduleCF.image.gatsbyImageData}
                alt={stepRescheduleCF.image.title}
                loading="eager"
                fadeIn={false}
                imgStyle={{ objectPosition: 'left' }}
              />
            )}
          </StyledImage>
        </div>
      </StyledRescheduleWrapper>
    </section>
  );
};

export default VariantA;

VariantA.propTypes = {
  stepRescheduleCF: PropTypes.shape({
    title: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
    image: PropTypes.shape({
      gatsbyImageData: PropTypes.shape({}).isRequired,
      title: PropTypes.string,
    }),
    successModal: PropTypes.object,
    surveyBeforeCancelling: PropTypes.object,
  }),
  userData: PropTypes.shape({
    locationCode: PropTypes.string,
    time: PropTypes.string,
    appointmentStatus: PropTypes.string,
  }),
  serviceName: PropTypes.string,
  goToNextStep: PropTypes.func,
  cancelBooking: PropTypes.func,
};
